'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface RegisterFormData {
  name: string;
  email: string;
  password: string;
  terms: boolean;
}

interface Props {
  onSwitchToLogin: () => void;
}

export default function RegisterForm({ onSwitchToLogin }: Props) {
  const router = useRouter();
  const { signUp, signInWithGoogle } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<RegisterFormData>();

  const handleGoogleSignUp = async () => {
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
      // Redirect handled by OAuth callback
    } catch (err: any) {
      toast.error(err?.message || 'Google sign-up failed. Please try again.');
      setGoogleLoading(false);
    }
  };

  const onSubmit = async (data: RegisterFormData) => {
    setLoading(true);
    try {
      await signUp(data.email, data.password, { fullName: data.name });
      toast.success('Welcome to Uyir! 💜');
      router.push('/home-chat-interface');
      router.refresh();
    } catch (err: any) {
      const message = err?.message || 'Sign up failed';
      if (message.toLowerCase().includes('already registered') || message.toLowerCase().includes('already exists')) {
        setError('email', { message: 'This email is already registered. Try signing in.' });
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold text-foreground mb-2">Join Uyir 💜</h1>
      <p className="text-muted-foreground mb-8">
        A safe space, just for you. Let's get you set up.
      </p>

      <button
        type="button"
        disabled={googleLoading}
        className="w-full flex items-center justify-center gap-3 px-4 py-3.5 rounded-2xl glass border border-[var(--glass-border)] text-foreground text-sm font-medium hover:bg-primary/5 active:scale-[0.98] transition-all duration-150 mb-6 disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
        onClick={handleGoogleSignUp}
        aria-label={googleLoading ? 'Signing up with Google…' : 'Sign up with Google'}
        aria-busy={googleLoading}
      >
        {googleLoading ? (
          <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" aria-hidden="true">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
          </svg>
        )}
        Sign up with Google
      </button>

      <div className="flex items-center gap-3 mb-6" aria-hidden="true">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground">or</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" aria-label="Create account with email and password" noValidate>
        <div>
          <label htmlFor="register-name" className="block text-sm font-medium text-foreground mb-1.5">
            Your name
          </label>
          <input
            id="register-name"
            type="text"
            {...register('name', { required: 'Name is required' })}
            placeholder="Arjun"
            autoComplete="name"
            aria-required="true"
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'register-name-error' : undefined}
            className="w-full px-4 py-3 rounded-xl bg-input border border-[var(--glass-border)] text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus-visible:border-primary/60 focus-visible:ring-2 focus-visible:ring-primary/30 transition-colors"
          />
          {errors.name && <p id="register-name-error" role="alert" className="mt-1.5 text-xs text-red-400">{errors.name.message}</p>}
        </div>

        <div>
          <label htmlFor="register-email" className="block text-sm font-medium text-foreground mb-1.5">
            Email
          </label>
          <input
            id="register-email"
            type="email"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^\S+@\S+$/, message: 'Enter a valid email' },
            })}
            placeholder="arjun@example.com"
            autoComplete="email"
            aria-required="true"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'register-email-error' : undefined}
            className="w-full px-4 py-3 rounded-xl bg-input border border-[var(--glass-border)] text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus-visible:border-primary/60 focus-visible:ring-2 focus-visible:ring-primary/30 transition-colors"
          />
          {errors.email && <p id="register-email-error" role="alert" className="mt-1.5 text-xs text-red-400">{errors.email.message}</p>}
        </div>

        <div>
          <label htmlFor="register-password" className="block text-sm font-medium text-foreground mb-1.5">
            Password
          </label>
          <p id="register-password-hint" className="text-xs text-muted-foreground mb-1.5">At least 8 characters</p>
          <div className="relative">
            <input
              id="register-password"
              type={showPassword ? 'text' : 'password'}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Minimum 8 characters' },
              })}
              placeholder="••••••••"
              autoComplete="new-password"
              aria-required="true"
              aria-invalid={!!errors.password}
              aria-describedby={`register-password-hint${errors.password ? ' register-password-error' : ''}`}
              className="w-full px-4 py-3 pr-12 rounded-xl bg-input border border-[var(--glass-border)] text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus-visible:border-primary/60 focus-visible:ring-2 focus-visible:ring-primary/30 transition-colors"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-1 rounded"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
            >
              {showPassword ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
            </button>
          </div>
          {errors.password && <p id="register-password-error" role="alert" className="mt-1.5 text-xs text-red-400">{errors.password.message}</p>}
        </div>

        <div>
          <label htmlFor="register-terms" className="flex items-start gap-3 cursor-pointer">
            <input
              id="register-terms"
              type="checkbox"
              {...register('terms', { required: 'You must agree to the terms' })}
              className="w-4 h-4 mt-0.5 rounded border-border accent-primary shrink-0"
              aria-required="true"
              aria-invalid={!!errors.terms}
              aria-describedby={errors.terms ? 'register-terms-error' : undefined}
            />
            <span className="text-sm text-muted-foreground leading-relaxed">
              I agree to the{' '}
              <a href="/terms" className="text-primary hover:underline focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-1 rounded">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy-policy" className="text-primary hover:underline focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-1 rounded">Privacy Policy</a>
            </span>
          </label>
          {errors.terms && <p id="register-terms-error" role="alert" className="mt-1.5 text-xs text-red-400">{errors.terms.message}</p>}
        </div>

        <button
          type="submit"
          disabled={loading}
          aria-busy={loading}
          className="w-full py-3.5 rounded-2xl gradient-bg text-white font-semibold text-sm disabled:opacity-60 hover:opacity-90 active:scale-[0.98] transition-all duration-150 focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2"
          style={{ minHeight: '52px' }}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Creating account…
            </span>
          ) : (
            'Create account'
          )}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-6">
        Already have an account?{' '}
        <button
          onClick={onSwitchToLogin}
          className="text-primary hover:underline font-medium focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-1 rounded"
        >
          Sign in
        </button>
      </p>
    </div>
  );
}